/**
 * imageGen.ts — the rules for AI pictures, in one place the server, the screens and the tests all read.
 *
 * What a picture is for decides everything: its shape, where empty space is left for words, and how the label
 * (AI-provenance data) is handled. People write one short description; `buildImagePrompt` turns it into the structured
 * prompt the image model is given. The model never draws words, logos or product screens: those are added in code
 * afterwards, so they are always exact. Sizes follow each platform's published specs (Meta, LinkedIn, Google).
 */
import { z } from "zod";

export const IMAGE_GROUPS = ["content", "ad", "social"] as const;
export type ImageGroup = (typeof IMAGE_GROUPS)[number];
export const IMAGE_GROUP_LABEL: Record<ImageGroup, string> = { content: "Articles", ad: "Ads", social: "Social posts" };

export type CopySpace = "none" | "left" | "top" | "bottom";
export type NativeShape = "square" | "landscape" | "portrait";

export interface ImagePurposeSpec {
  id: string;
  group: ImageGroup;
  /** Short name on the picker. */
  label: string;
  /** Where it will be used, in plain words. */
  hint: string;
  width: number;
  height: number;
  /** The shape the model is asked for; the result is then cropped to exactly width × height. */
  native: NativeShape;
  /** Aspect ratio sent to Gemini (it takes a ratio, not pixels). */
  ratio: "1:1" | "4:5" | "16:9" | "3:2" | "9:16";
  /** Where the model keeps the picture quiet so a headline can sit there. */
  copySpace: CopySpace;
}

export const IMAGE_PURPOSES = [
  { id: "article_cover", group: "content", label: "Article cover", hint: "Top of a blog article", width: 1600, height: 900, native: "landscape", ratio: "16:9", copySpace: "none" },
  { id: "article_inline", group: "content", label: "Inside an article", hint: "Between paragraphs", width: 1200, height: 800, native: "landscape", ratio: "3:2", copySpace: "none" },
  { id: "ad_square", group: "ad", label: "Ad · Square", hint: "Meta and LinkedIn feed", width: 1080, height: 1080, native: "square", ratio: "1:1", copySpace: "top" },
  { id: "ad_portrait", group: "ad", label: "Ad · Portrait", hint: "Meta and LinkedIn mobile feed", width: 1080, height: 1350, native: "portrait", ratio: "4:5", copySpace: "top" },
  { id: "ad_scene", group: "ad", label: "Ad · Scene", hint: "Picture behind the words of a LinkedIn ad", width: 1080, height: 1080, native: "square", ratio: "1:1", copySpace: "top" },
  { id: "ad_landscape", group: "ad", label: "Ad · Wide", hint: "LinkedIn, Meta links, Google", width: 1200, height: 628, native: "landscape", ratio: "16:9", copySpace: "left" },
  { id: "ad_story", group: "ad", label: "Ad · Story", hint: "Stories and Reels", width: 1080, height: 1920, native: "portrait", ratio: "9:16", copySpace: "top" },
  { id: "social_square", group: "social", label: "Post · Square", hint: "Instagram, Facebook, LinkedIn", width: 1080, height: 1080, native: "square", ratio: "1:1", copySpace: "none" },
  { id: "social_wide", group: "social", label: "Post · Wide", hint: "X, LinkedIn, Facebook links", width: 1200, height: 675, native: "landscape", ratio: "16:9", copySpace: "none" },
  { id: "social_story", group: "social", label: "Post · Story", hint: "Stories and Reels", width: 1080, height: 1920, native: "portrait", ratio: "9:16", copySpace: "none" },
] as const satisfies readonly ImagePurposeSpec[];

export type ImagePurposeId = (typeof IMAGE_PURPOSES)[number]["id"];
export const IMAGE_PURPOSE_IDS = IMAGE_PURPOSES.map((p) => p.id) as [ImagePurposeId, ...ImagePurposeId[]];
export const imagePurposeSchema = z.enum(IMAGE_PURPOSE_IDS);

/** A purpose with its exact id type (so a picture's `purpose` can be looked up without casting). */
export type PurposeSpec = (typeof IMAGE_PURPOSES)[number];
export function purposeSpec(id: ImagePurposeId): PurposeSpec {
  return IMAGE_PURPOSES.find((p) => p.id === id)!;
}
export function purposesFor(group: ImageGroup): PurposeSpec[] {
  return IMAGE_PURPOSES.filter((p) => p.group === group);
}

/** What part of a picture to keep when filling a box exactly (like CSS object-fit: cover, centred). */
export function coverCrop(srcW: number, srcH: number, dstW: number, dstH: number): { x: number; y: number; w: number; h: number } {
  const want = dstW / dstH;
  const have = srcW / srcH;
  if (have > want) {
    const w = Math.round(srcH * want);
    return { x: Math.round((srcW - w) / 2), y: 0, w, h: srcH };
  }
  const h = Math.round(srcW / want);
  return { x: 0, y: Math.round((srcH - h) / 2), w: srcW, h };
}

// ---------- people ----------

export const PEOPLE_DESCRIPTION_MAX = 200;
export const imagePeopleSchema = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("none") }),
  z.object({ mode: z.literal("described"), description: z.string().trim().min(5, "Describe who should be in the picture.").max(PEOPLE_DESCRIPTION_MAX) }),
]);
export type ImagePeople = z.infer<typeof imagePeopleSchema>;

// ---------- brand ----------

export const imageBrandSchema = z.object({
  /** Up to 4 hex colours the picture should lean on. */
  colors: z.array(z.string().regex(/^#[0-9a-fA-F]{6}$/, "Use a colour like #0080FF.")).max(4).default([]),
  /** The look, in a sentence ("clean, bright, modern workplace photography"). */
  style: z.string().trim().max(240).default(""),
  /** Things never to show. */
  avoid: z.string().trim().max(240).default(""),
});
export type ImageBrand = z.infer<typeof imageBrandSchema>;
export const DEFAULT_IMAGE_STYLE = "clean, modern, natural light, professional business setting, uncluttered";

// ---------- the prompt ----------

export const BRIEF_MIN = 5;
export const BRIEF_MAX = 600;

export interface PromptInput {
  purpose: ImagePurposeId;
  brief: string;
  brand?: Partial<ImageBrand>;
  people?: ImagePeople;
}

const COPY_SPACE_TEXT: Record<CopySpace, string> = {
  none: "Compose the whole frame naturally.",
  left: "Keep the left third of the frame quiet and uncluttered (a soft, plain area), so a headline can be placed there later. Put the subject on the right.",
  top: "Keep the top third of the frame quiet and uncluttered (a soft, plain area), so a headline can be placed there later. Put the subject in the lower two thirds.",
  bottom: "Keep the bottom third of the frame quiet and uncluttered, so a headline can be placed there later. Put the subject in the upper two thirds.",
};

/** The structured prompt: scene, style, composition, then hard constraints. Same shape every time, so results are predictable. */
export function buildImagePrompt(input: PromptInput): string {
  const spec = purposeSpec(input.purpose);
  const brand = input.brand ?? {};
  const people = input.people ?? { mode: "none" };
  const colours = (brand.colors ?? []).filter(Boolean);
  const lines = [
    `Scene: ${input.brief.trim().replace(/\s+/g, " ")}`,
    `Use: ${spec.label} (${spec.hint}), ${spec.width}×${spec.height}.`,
    `Style: ${brand.style?.trim() || DEFAULT_IMAGE_STYLE}.`,
    colours.length ? `Colours: lean on ${colours.join(", ")}; keep everything else calm and neutral.` : "",
    `Composition: ${COPY_SPACE_TEXT[spec.copySpace]}`,
    people.mode === "none"
      ? "People: none. Show objects, places, or abstract shapes only. No faces, no hands, no silhouettes."
      : `People: ${people.description.trim()}. Natural, realistic, unnamed, not looking like any real person. Anatomically correct hands and faces.`,
    "Constraints: no text, letters, numbers or symbols anywhere; no logos or brand marks; no computer or phone screens showing software or charts; no watermarks; no collage or borders.",
    brand.avoid?.trim() ? `Never show: ${brand.avoid.trim()}.` : "",
  ];
  return lines.filter(Boolean).join("\n");
}

export const briefSchema = z
  .string()
  .trim()
  .min(BRIEF_MIN, "Describe the picture in a few words.")
  .max(BRIEF_MAX, "Keep it shorter.");

// ---------- the label (AI-provenance data) ----------

export const IMAGE_LABEL_MODES = ["keep", "remove"] as const;
export type ImageLabelMode = (typeof IMAGE_LABEL_MODES)[number];
export const imageLabelPolicySchema = z.object({
  content: z.enum(IMAGE_LABEL_MODES),
  ad: z.enum(IMAGE_LABEL_MODES),
  social: z.enum(IMAGE_LABEL_MODES),
});
export type ImageLabelPolicy = z.infer<typeof imageLabelPolicySchema>;
export const DEFAULT_LABEL_POLICY: ImageLabelPolicy = { content: "keep", ad: "remove", social: "keep" };

/**
 * What is done with the label on a finished picture. "Remove" saves the picture re-encoded (the data is not carried
 * over); "keep" saves the untouched original. A picture with people always keeps it: realistic people are what
 * platforms and the EU AI Act most want labelled. Invisible watermarks the model adds cannot be removed either way.
 */
export function labelDecision(policy: ImageLabelPolicy, group: ImageGroup, people: ImagePeople): { mode: ImageLabelMode; forced: boolean; reason: string } {
  if (people.mode === "described") return { mode: "keep", forced: true, reason: "Pictures with people keep the AI label." };
  const mode = policy[group];
  return { mode, forced: false, reason: mode === "keep" ? "The AI label is kept." : "The AI label is removed from the saved picture." };
}

// ---------- quality, cost, limits ----------

export const IMAGE_QUALITIES = ["draft", "final"] as const;
export type ImageQuality = (typeof IMAGE_QUALITIES)[number];
/** Drafts are cheaper; the chosen one is re-made at final quality. */

export const MAX_VARIANTS = 4;
export const DEFAULT_VARIANTS = 3;
export const DEFAULT_MONTHLY_CAP_USD = 25;
/** The most one request may cost, worst case, estimated before anything is generated. */
export const WORST_CASE_USD: Record<ImageQuality, number> = { draft: 0.1, final: 0.35 };

/** Rates in US dollars per million tokens. Google: from a third-party listing, so treat as an estimate. */
export const IMAGE_TOKEN_RATES = {
  google: { input: 0.5, output: 3 },
} as const;

export function costFromUsage(provider: keyof typeof IMAGE_TOKEN_RATES, usage: { inputTokens: number; outputTokens: number }): number {
  const r = IMAGE_TOKEN_RATES[provider];
  const usd = (usage.inputTokens * r.input + usage.outputTokens * r.output) / 1_000_000;
  return Math.round(usd * 10_000) / 10_000;
}

export function worstCaseFor(quality: ImageQuality, variants: number): number {
  return Math.round(WORST_CASE_USD[quality] * variants * 100) / 100;
}

// ---------- review of a finished picture (a second look by a vision model) ----------

export const imageReviewSchema = z.object({
  matchesBrief: z.boolean(),
  hasReadableText: z.boolean(),
  hasLogoOrScreen: z.boolean(),
  hasPeople: z.boolean(),
  hasArtifacts: z.boolean(),
  notes: z.array(z.string().max(160)).max(4),
});
export type ImageReview = z.infer<typeof imageReviewSchema>;

export interface ReviewVerdict {
  pass: boolean;
  /** Plain-language reasons it failed, shortest first. */
  problems: string[];
}

export function reviewVerdict(review: ImageReview, people: ImagePeople): ReviewVerdict {
  const problems: string[] = [];
  if (!review.matchesBrief) problems.push("Doesn't match what you asked for");
  if (review.hasReadableText) problems.push("Contains writing");
  if (review.hasLogoOrScreen) problems.push("Shows a logo or software screen");
  if (review.hasPeople && people.mode === "none") problems.push("Shows people you didn't ask for");
  if (!review.hasPeople && people.mode === "described") problems.push("The people you described are missing");
  if (review.hasArtifacts) problems.push("Looks distorted");
  return { pass: problems.length === 0, problems };
}

// ---------- the stored picture, as the screens see it ----------

export const IMAGE_STATUSES = ["ready", "failed"] as const;
export const generatedImageSchema = z.object({
  id: z.string(),
  siteId: z.string(),
  purpose: imagePurposeSchema,
  brief: z.string(),
  peopleDescription: z.string().nullable(),
  provider: z.enum(["openai", "google", "higgsfield", "upload"]),
  model: z.string(),
  quality: z.enum(IMAGE_QUALITIES),
  status: z.enum(IMAGE_STATUSES),
  /** Plain-language reason a picture failed, or the review's problems. */
  problems: z.array(z.string()),
  reviewPassed: z.boolean().nullable(),
  labelKept: z.boolean(),
  costUsd: z.number(),
  parentId: z.string().nullable(),
  altText: z.string(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
});
export type GeneratedImage = z.infer<typeof generatedImageSchema>;

export const generateImagesRequestSchema = z.object({
  siteId: z.string().min(1),
  purpose: imagePurposeSchema,
  brief: briefSchema,
  people: imagePeopleSchema.default({ mode: "none" }),
  quality: z.enum(IMAGE_QUALITIES).default("draft"),
  variants: z.number().int().min(1).max(MAX_VARIANTS).default(DEFAULT_VARIANTS),
  /** "More like this" and "Change this": the picture to start from, and what to change (one change at a time). */
  fromImageId: z.string().optional(),
  change: z.string().trim().max(300).optional(),
});
export type GenerateImagesRequest = z.infer<typeof generateImagesRequestSchema>;

export const generateImagesResponseSchema = z.object({
  images: z.array(generatedImageSchema),
  /** What the whole request cost, in US dollars. */
  costUsd: z.number(),
  monthSpentUsd: z.number(),
  monthCapUsd: z.number(),
});
export type GenerateImagesResponse = z.infer<typeof generateImagesResponseSchema>;

export const imageSettingsSchema = z.object({
  brand: imageBrandSchema,
  labelPolicy: imageLabelPolicySchema,
  monthlyCapUsd: z.number().min(0).max(10_000),
});
export type ImageSettings = z.infer<typeof imageSettingsSchema>;
export const DEFAULT_IMAGE_SETTINGS: ImageSettings = {
  brand: { colors: [], style: "", avoid: "" },
  labelPolicy: DEFAULT_LABEL_POLICY,
  monthlyCapUsd: DEFAULT_MONTHLY_CAP_USD,
};

// ---------- the private call from the CMS to the AI Worker ----------

export const IMAGE_PROVIDERS = ["google", "higgsfield"] as const;
export type ImageProvider = (typeof IMAGE_PROVIDERS)[number];
export const IMAGE_PROVIDER_LABEL: Record<ImageProvider, string> = { google: "Google", higgsfield: "Higgsfield" };
/** Which service draws first when its key is saved; the others are the backup, in the order of IMAGE_PROVIDERS. */
export const DEFAULT_IMAGE_PROVIDER: ImageProvider = "google";

/**
 * The Higgsfield picture models this CMS can use. Each id is the model's text-to-image address in the Higgsfield API
 * (https://docs.higgsfield.ai, "Image generation"), the part after https://api.higgsfield.ai. Only models whose request
 * fields were read from the official model pages are listed; adding one means reading its page first.
 */
export const HIGGSFIELD_IMAGE_MODELS = [
  { id: "recraft-v4-1", label: "Recraft V4.1", path: "recraft/v4.1/text-to-image", hint: "Clean, versatile 1K pictures. Every shape we use is supported." },
  { id: "soul-2", label: "Soul V2", path: "higgsfield-ai/soul/v2/standard", hint: "Photographic people, fashion and editorial looks. 720p drafts, 1080p finals." },
  { id: "ideogram-4", label: "Ideogram 4.0", path: "ideogram/v4.0", hint: "Strong composition. Turbo drafts, Quality finals." },
] as const;
export type HiggsfieldImageModelId = (typeof HIGGSFIELD_IMAGE_MODELS)[number]["id"];
export const HIGGSFIELD_IMAGE_MODEL_IDS = HIGGSFIELD_IMAGE_MODELS.map((m) => m.id) as [HiggsfieldImageModelId, ...HiggsfieldImageModelId[]];
export const DEFAULT_HIGGSFIELD_IMAGE_MODEL: HiggsfieldImageModelId = "recraft-v4-1";

export const imageCallRequestSchema = z.object({
  provider: z.enum(IMAGE_PROVIDERS),
  prompt: z.string().min(10).max(4000),
  shape: z.enum(["square", "landscape", "portrait"]),
  ratio: z.enum(["1:1", "4:5", "16:9", "3:2", "9:16"]),
  quality: z.enum(IMAGE_QUALITIES),
});
export type ImageCallRequest = z.infer<typeof imageCallRequestSchema>;

export const imageCallResponseSchema = z.object({
  /** The picture exactly as the provider returned it (PNG), base64. */
  imageBase64: z.string().min(1),
  mimeType: z.string(),
  model: z.string(),
  usage: z.object({ inputTokens: z.number(), outputTokens: z.number() }),
  /** The price in US dollars when the provider states one (Higgsfield does); otherwise it is worked out from `usage`. */
  costUsd: z.number().min(0).optional(),
});
export type ImageCallResponse = z.infer<typeof imageCallResponseSchema>;

export const imageReviewRequestSchema = z.object({
  imageBase64: z.string().min(1),
  mimeType: z.string(),
  brief: z.string().max(BRIEF_MAX),
  people: imagePeopleSchema,
});
export type ImageReviewRequest = z.infer<typeof imageReviewRequestSchema>;

export const IMAGE_REVIEW_INSTRUCTIONS =
  "You check one AI-generated marketing picture against the brief. Answer only with the JSON. " +
  "matchesBrief: the picture clearly shows what the brief describes. hasReadableText: any readable letters, words or numbers appear anywhere. " +
  "hasLogoOrScreen: a logo, brand mark, or a screen showing software, charts or a user interface appears. hasPeople: any person, face or hand appears. " +
  "hasArtifacts: obvious distortion such as extra or missing fingers, melted faces, warped objects or broken geometry. " +
  "notes: up to 4 very short plain-language remarks. Be strict.";
